import { db, authReady } from './firebase'
import {
  collection, doc, getDoc, getDocs, setDoc, deleteDoc,
  query, where, onSnapshot, serverTimestamp, Timestamp,
} from 'firebase/firestore'
import { getSession } from './auth'

export interface Friend {
  uid: string
  username: string
  label: string
  currentVibe?: string | null
  currentVibeLabel?: string | null
  friendshipId: string
  streak: number
  streakTier: string
}

export interface Knock {
  id: string
  from: string
  fromName: string
  createdAt?: Timestamp
  expiresAt?: Timestamp
}

export type RangeStatus = 'out' | 'muted'

// ─── Find a user by username ─────────────────────────────────────────────────
export async function findUserByUsername(username: string) {
  await authReady
  const clean = username.trim().toLowerCase()
  if (!clean) return null
  const snap = await getDoc(doc(db, 'usernames', clean))
  if (!snap.exists()) return null
  const { uid } = snap.data()
  const userSnap = await getDoc(doc(db, 'users', uid))
  if (!userSnap.exists()) return null
  const d = userSnap.data()
  return { uid, username: d.username as string }
}

// ─── THE KNOCK — an invitation, not a friendship ─────────────────────────────
export async function sendKnock(theirUid: string):
  Promise<'knocked' | 'already-friends' | 'already-knocked'> {
  await authReady
  const session = getSession()
  if (!session) throw new Error('Not signed in')

  const friendshipId = [session.uid, theirUid].sort().join('_')
  const fSnap = await getDoc(doc(db, 'friendships', friendshipId))
  if (fSnap.exists()) return 'already-friends'

  const knockId = `${session.uid}_${theirUid}`
  const kSnap = await getDoc(doc(db, 'knocks', knockId))
  if (kSnap.exists()) return 'already-knocked'

  const expiresAt = new Date()
  expiresAt.setHours(expiresAt.getHours() + 48)

  await setDoc(doc(db, 'knocks', knockId), {
    from: session.uid,
    to: theirUid,
    fromName: session.username,
    createdAt: serverTimestamp(),
    expiresAt: Timestamp.fromDate(expiresAt),
  })
  return 'knocked'
}

// ─── Knocks at my door (live) ────────────────────────────────────────────────
export function listenToKnocks(
  onUpdate: (knocks: Knock[]) => void,
  onError?: (msg: string) => void
) {
  const session = getSession()
  if (!session) return () => {}
  const q = query(collection(db, 'knocks'), where('to', '==', session.uid))
  return onSnapshot(q, (snap) => {
    const now = Date.now()
    const knocks = snap.docs
      .map((d) => ({ id: d.id, ...d.data() } as Knock))
      .filter((k) => !k.expiresAt || k.expiresAt.toDate().getTime() > now)
    onUpdate(knocks)
  }, (err) => {
    console.error('knocks listen', err)
    onError?.((err as any)?.code || err.message)
  })
}

// ─── Let a knock fade (no reject — it just dissolves) ────────────────────────
export async function dismissKnock(knockId: string) {
  await authReady
  await deleteDoc(doc(db, 'knocks', knockId)).catch(() => {})
}

// ─── Live squad ──────────────────────────────────────────────────────────────
export function listenToFriends(
  onUpdate: (friends: Friend[]) => void,
  onError?: (msg: string) => void
) {
  const session = getSession()
  if (!session) return () => {}
  const q = query(
    collection(db, 'friendships'),
    where('users', 'array-contains', session.uid)
  )
  return onSnapshot(q, async (snap) => {
    try {
      const labelsSnap = await getDocs(
        collection(db, 'users', session.uid, 'friendLabels')
      ).catch(() => null)
      const labels: Record<string, string> = {}
      labelsSnap?.forEach((d) => { labels[d.id] = (d.data() as any).label })

      const friends: Friend[] = []
      for (const f of snap.docs) {
        const data = f.data()
        const otherUid = (data.users as string[]).find((u) => u !== session.uid)
        if (!otherUid) continue
        const userSnap = await getDoc(doc(db, 'users', otherUid)).catch(() => null)
        if (!userSnap || !userSnap.exists()) continue
        const u = userSnap.data()
        friends.push({
          uid: otherUid,
          username: u.username,
          label: labels[otherUid] || u.username,
          currentVibe: u.currentVibe || null,
          currentVibeLabel: u.currentVibeLabel || null,
          friendshipId: f.id,
          streak: data.streak || 0,
          streakTier: data.streakTier || 'none',
        })
      }
      friends.sort((a, b) => a.label.localeCompare(b.label))
      onUpdate(friends)
    } catch (e: any) {
      console.error('friends inner', e)
      onError?.(e?.code || e?.message || 'friends load failed')
    }
  }, (err) => {
    console.error('friends listen', err)
    onError?.((err as any)?.code || err.message || 'friends listen denied')
  })
}

// ─── Rename (local only) ─────────────────────────────────────────────────────
export async function renameFriend(friendUid: string, label: string) {
  const session = getSession()
  if (!session) return
  await setDoc(doc(db, 'users', session.uid, 'friendLabels', friendUid), { label })
}

// ─── RANGE CONTROL — quiet, invisible, reversible ────────────────────────────
// 'out'   → their tingls dissolve into the void. they never know.
// 'muted' → tingls land in your feed, but never buzz you.
export async function setRange(friendUid: string, status: RangeStatus | null) {
  await authReady
  const session = getSession()
  if (!session) return
  const ref = doc(db, 'users', session.uid, 'range', friendUid)
  if (status === null) await deleteDoc(ref).catch(() => {})
  else await setDoc(ref, { status, at: serverTimestamp() })
}

export function listenToRange(
  onUpdate: (map: Record<string, RangeStatus>) => void
) {
  const session = getSession()
  if (!session) return () => {}
  return onSnapshot(collection(db, 'users', session.uid, 'range'), (snap) => {
    const map: Record<string, RangeStatus> = {}
    snap.forEach((d) => { map[d.id] = (d.data() as any).status })
    onUpdate(map)
  }, () => {})
}

// ─── RELEASE — mutual dissolution + 90-day archive for easy re-knock ────────
export async function releaseFriend(friend: Friend) {
  await authReady
  const session = getSession()
  if (!session) return
  const expiresAt = new Date()
  expiresAt.setDate(expiresAt.getDate() + 90)
  await setDoc(doc(db, 'users', session.uid, 'archive', friend.uid), {
    username: friend.username,
    label: friend.label,
    releasedAt: serverTimestamp(),
    expiresAt: Timestamp.fromDate(expiresAt),
  })
  await deleteDoc(doc(db, 'friendships', friend.friendshipId))
}

// ─── ARCHIVE — released friends, 90 days to re-knock ─────────────────────────
export interface ArchivedFriend {
  uid: string
  username: string
  label: string
  expiresAt?: Timestamp
}

export function listenToArchive(onUpdate: (list: ArchivedFriend[]) => void) {
  const session = getSession()
  if (!session) return () => {}
  return onSnapshot(collection(db, 'users', session.uid, 'archive'), (snap) => {
    const now = Date.now()
    const list = snap.docs
      .map((d) => ({ uid: d.id, ...d.data() } as ArchivedFriend))
      .filter((a) => !a.expiresAt || a.expiresAt.toDate().getTime() > now)
    onUpdate(list)
  }, () => {})
}

export async function removeFromArchive(uid: string) {
  const session = getSession()
  if (!session) return
  await deleteDoc(doc(db, 'users', session.uid, 'archive', uid)).catch(() => {})
}
